import { getLogger } from '@deltachat-desktop/shared/logger'

const log = getLogger('renderer/utils/mediaMetadata')

/** Only headers are read here, no frames are decoded, so this is much quicker than a poster
 * grab — but a send waits on it, so it still gets a ceiling. */
const TIMEOUT_MS = 3_000

/** Core drops incoming durations of a day or more (`mimeparser`), so there is no point
 * sending one. Also catches the `Infinity`/garbage a broken container can report. */
const MAX_DURATION_MS = 24 * 60 * 60 * 1000

/** Resolves on `event`, rejects on the element's `error` or on abort. */
export function once(
  el: HTMLMediaElement,
  event: string,
  signal: AbortSignal
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    el.addEventListener(event, () => resolve(), { once: true, signal })
    el.addEventListener('error', () => reject(el.error), { once: true, signal })
    signal.addEventListener('abort', () => reject(signal.reason), {
      once: true,
    })
  })
}

export type MediaMetadata = {
  width?: number
  height?: number
  /** milliseconds */
  duration?: number
}

/**
 * Reads the pixel size and duration of the audio or video file at `url`.
 *
 * The browser ships a demuxer for everything it can play, so this needs neither a codec nor
 * a dependency of our own — which is also why it cannot live in core, whose wasm runs in a
 * worker with no DOM. Core measures images itself; those never need this.
 *
 * Every field is independently optional: whatever the browser could not tell us is simply
 * left out, and an undecodable file yields `{}` rather than an error. Callers use this to
 * enrich a message, never to gate one.
 */
export async function mediaMetadata(url: string): Promise<MediaMetadata> {
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(new Error('timeout')), TIMEOUT_MS)
  // A <video> loads audio-only files just fine (`videoWidth` stays 0), so one element
  // covers both and the caller does not have to know which it has.
  const el = document.createElement('video')
  const meta: MediaMetadata = {}
  try {
    el.muted = true
    el.preload = 'metadata'
    el.src = url
    await once(el, 'loadedmetadata', abort.signal)

    if (el.videoWidth > 0 && el.videoHeight > 0) {
      meta.width = el.videoWidth
      meta.height = el.videoHeight
    }
    const durationMs = Math.round((await readDuration(el, abort.signal)) * 1000)
    if (durationMs > 0 && durationMs < MAX_DURATION_MS) {
      meta.duration = durationMs
    }
  } catch (error) {
    log.warn('could not read media metadata:', error)
  } finally {
    clearTimeout(timer)
    abort.abort()
    // Drop the demuxer and let the browser release whatever it buffered.
    el.removeAttribute('src')
    el.load()
  }
  return meta
}

/**
 * Duration in seconds, or NaN.
 *
 * Containers written by a live stream carry no duration in their header — MediaRecorder
 * webm, and mp3 encoded without a Xing frame, which is what our own voice recorder
 * produces. They report `Infinity` until something makes the browser scan to the end;
 * seeking past any possible end is the way to ask for that. The position itself is
 * irrelevant, the element is thrown away right after.
 */
async function readDuration(
  el: HTMLMediaElement,
  signal: AbortSignal
): Promise<number> {
  if (Number.isFinite(el.duration)) {
    return el.duration
  }
  try {
    el.currentTime = 1e101
    await once(el, 'durationchange', signal)
  } catch (error) {
    // A file we cannot measure still has a usable size, and for a video the pixels are
    // already read by now — keep those rather than failing the whole lookup.
    log.warn('could not resolve an open-ended duration:', error)
  }
  return el.duration
}
